/*
 * HTML sanitization utilities
 */

import { tagRegistry } from './tag-registry';

/**
 * A default set of allowed HTML tags for sanitization
 * @memberof Sanitizer
 * @constant {Set<string>}
 */
export const SANITIZER_DEFAULT_ALLOWED_TAGS = new Set([
    'a',
    'abbr',
    'address',
    'area',
    'article',
    'aside',
    'b',
    'blockquote',
    'br',
    'caption',
    'cite',
    'code',
    'col',
    'colgroup',
    'dd',
    'del',
    'details',
    'dfn',
    'div',
    'dl',
    'dt',
    'em',
    'figcaption',
    'figure',
    'footer',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'header',
    'hgroup',
    'hr',
    'i',
    'ins',
    'kbd',
    'li',
    'main',
    'mark',
    'meter',
    'nav',
    'nobr',
    'ol',
    'output',
    'p',
    'pre',
    'progress',
    'q',
    's',
    'samp',
    'section',
    'small',
    'span',
    'strong',
    'sub',
    'summary',
    'sup',
    'table',
    'tbody',
    'td',
    'template',
    'tfoot',
    'th',
    'thead',
    'time',
    'tr',
    'u',
    'ul',
]);

/**
 * A default set of allowed HTML attributes for sanitization
 * @memberof Sanitizer
 * @constant {Set<string>}
 */
export const SANITIZER_DEFAULT_ALLOWED_ATTRS = new Set([
    'class',
    'id',
    'title',
    'role',
    'tabindex',
    'lang',
    'hidden',
    'spellcheck',
    'slot',
    'dir',
]);

/**
 * A default set of allowed SVG tags for sanitization. This set is based on and utilized by the SVG sprites currently used in the library, but can be used as a base or replaced.
 * @memberof Sanitizer
 * @constant {Set<string>}
 */
export const SANITIZER_DEFAULT_ALLOWED_TAGS_SVG = new Set([
    'svg',
    'symbol',
    'path',
    'circle',
    'defs',
    'g',
    'image',
    'linearGradient',
    'stop',
]);

/**
 * A default set of allowed SVG attributes for sanitization. This set is based on and utilized by the SVG sprites currently used in the library, but can be used as a base or replaced.
 * @memberof Sanitizer
 * @constant {Set<string>}
 */
export const SANITIZER_DEFAULT_ALLOWED_ATTRS_SVG = new Set([
    'clip-rule',
    'cx',
    'cy',
    'd',
    'fill',
    'fill-opacity',
    'fill-rule',
    'gradientUnits',
    'height',
    'id',
    'offset',
    'opacity',
    'r',
    'stop-color',
    'stop-opacity',
    'stroke',
    'stroke-miterlimit',
    'stroke-width',
    'style',
    'viewBox',
    'width',
    'x',
    'x1',
    'x2',
    'xlink:href',
    'xmlns',
    'xmlns:xlink',
    'y',
    'y1',
    'y2',
]);

/**
 * Allowed attributes for specific tags
 */
const TAG_SPECIFIC_ATTRS: Record<string, Set<string>> = {
    a: new Set(['href', 'target', 'rel']),
    time: new Set(['datetime']),
};

/**
 * Protocols allowed in href and src attributes
 */
const ALLOWED_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

/**
 * Attributes that contain URLs and should be validated for safe protocols
 */
const URL_ATTRIBUTES = new Set([
    'href',
    'src',
    'xlink:href',
    'action',
    'formaction',
]);

const ALWAYS_UNSAFE_TAGS = new Set([
    'script',
    'style',
    'iframe',
    'object',
    'embed',
]);

let cachedAllowedCustomTags: Set<string> | null = null;

const getAllowedCustomTags = (): Set<string> => {
    cachedAllowedCustomTags ??= new Set(
        tagRegistry.getAll().map((tagName) => tagName.toLowerCase())
    );
    return cachedAllowedCustomTags;
};

// Note this currently doesn't contain any ui-chart specific elements
const isAllowedCustomElement = (tagName: string): boolean =>
    getAllowedCustomTags().has(tagName.toLowerCase());

const isAlwaysUnsafeAttribute = (
    attrName: string,
    attrValue: string
): boolean => {
    if (attrName.startsWith('on')) {
        return true;
    }

    const value = attrValue.trim().toLowerCase();
    // eslint-disable-next-line no-script-url
    if (value.startsWith('javascript:')) {
        return true;
    }

    return false;
};

/**
 * Sanitizes HTML string by cleaning up tags and attributes.
 * @memberof Sanitizer
 * @function
 * @param {string} html - The HTML string to sanitize
 * @param {Set<string> | false} [allowedTags] - A set of allowed tags. Set to false to skip tag validation (except for a short list of dangerous tags that are always blocked)
 * @param {Set<string> | false} [allowedAttrs] - A set of allowed attributes (applies to all tags). Set to false to allow all attributes (except always-unsafe ones)
 * @returns {string} Sanitized HTML string
 * @example
 * // Allow only default tags and attributes (includes UI library custom elements)
 * const unsafeHTML = '<p onclick="alert(\'XSS\')" class="paragraph">Click <button>me</button> to go to <ui-link href="swedbank.ee" label="Swedbank.ee"></ui-link></p>';
 * const safeHTML = SharedComponents.sanitizer.sanitizeHTML(unsafeHTML);
 * // Outputs: '<p class="paragraph">Click me to go to <ui-link href="swedbank.ee" label="Swedbank.ee"></ui-link></p>'
 * @example
 * // Allow only button tag
 * const unsafeHTML = '<p onclick="alert(\'XSS\')" class="paragraph">Click <button>me</button></p>';
 * const safeHTML = SharedComponents.sanitizer.sanitizeHTML(unsafeHTML, new Set(['button']));
 * // Outputs: 'Click <button>me</button>'
 * @example
 * // Allow default tags + button tag
 * const unsafeHTML = '<p onclick="alert(\'XSS\')" class="paragraph">Click <button>me</button><input name="test"></p>';
 * const safeHTML = SharedComponents.sanitizer.sanitizeHTML(unsafeHTML, new Set(['button', ...SharedComponents.sanitizer.SANITIZER_DEFAULT_ALLOWED_TAGS]));
 * // Outputs: '<p class="paragraph">Click <button>me</button></p>'
 * @example
 * // Allow some form-specific tags and attributes
 * const unsafeHTML = '<form action="submit"><input type="text" name="username" value="test"><button type="submit">Submit</button></form>';
 * const safeHTML = SharedComponents.sanitizer.sanitizeHTML(unsafeHTML, new Set(['form', 'input', 'button']), new Set(['type', 'name', 'value', 'action']));
 * // Outputs: '<form action="submit"><input type="text" name="username" value="test"><button type="submit">Submit</button></form>'
 * @example
 * // Disallow all tags
 * const unsafeHTML = '<p onclick="alert(\'XSS\')" class="paragraph">Click <button>me</button><input name="test"></p>';
 * const safeHTML = SharedComponents.sanitizer.sanitizeHTML(unsafeHTML, new Set([]));
 * // Outputs: 'Click me'
 * @example
 * // Allow all tags and attributes except for always-unsafe ones
 * const unsafeHTML = '<p onclick="alert(\'XSS\')" class="paragraph">Click <button>me</button><input name="test"></p>';
 * const safeHTML = SharedComponents.sanitizer.sanitizeHTML(unsafeHTML, false, false);
 * // Outputs: '<p class="paragraph">Click <button>me</button><input name="test"></p>'
 */
export const sanitizeHTML = (
    html: string,
    allowedTags: Set<string> | false = SANITIZER_DEFAULT_ALLOWED_TAGS,
    allowedAttrs: Set<string> | false = SANITIZER_DEFAULT_ALLOWED_ATTRS
): string => {
    if (!html || typeof html !== 'string') {
        return '';
    }

    if (typeof DOMParser === 'undefined' || typeof document === 'undefined') {
        return '';
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const baseUrl =
        typeof document !== 'undefined' && document.baseURI
            ? document.baseURI
            : 'http://localhost/';

    const sanitizeNode = (node: Node): Node | null => {
        if (node.nodeType === Node.TEXT_NODE) {
            return node.cloneNode(false);
        }

        if (node.nodeType !== Node.ELEMENT_NODE) {
            return null;
        }

        const element = node as Element;
        const tagName = element.tagName.toLowerCase();

        if (ALWAYS_UNSAFE_TAGS.has(tagName)) {
            return null;
        }

        const shouldValidateTags = allowedTags !== false;

        if (
            shouldValidateTags &&
            !allowedTags.has(tagName) &&
            !isAllowedCustomElement(tagName)
        ) {
            // Unwrap disallowed tags but keep sanitized children
            const fragment = document.createDocumentFragment();
            for (const child of Array.from(element.childNodes)) {
                const sanitizedChild = sanitizeNode(child);
                if (sanitizedChild) {
                    fragment.appendChild(sanitizedChild);
                }
            }
            return fragment.childNodes.length ? fragment : null;
        }

        const sanitized = document.createElement(tagName);

        // Sanitize attributes
        const attrs = Array.from(element.attributes);
        for (const attr of attrs) {
            const rawAttrName = attr.name;
            const attrName = rawAttrName.toLowerCase();
            let allowed = false;

            // For custom elements we allow any attribute except for always-unsafe ones
            if (isAllowedCustomElement(tagName)) {
                allowed = true;

                // Drop always-unsafe attributes for custom elements
                if (isAlwaysUnsafeAttribute(attrName, attr.value)) {
                    allowed = false;
                }
            } else {
                if (allowedAttrs === false) {
                    allowed = true;
                } else {
                    // For standard HTML tags, we allow attributes case-by-case using an allowlist
                    if (
                        allowedAttrs.has(attrName) ||
                        allowedAttrs.has(rawAttrName)
                    ) {
                        allowed = true;
                    }

                    if (attrName.startsWith('aria-')) {
                        allowed = true;
                    }

                    if (attrName.startsWith('data-')) {
                        allowed = true;
                    }

                    // Check tag-specific attributes
                    if (
                        TAG_SPECIFIC_ATTRS[tagName]?.has(attrName) ||
                        TAG_SPECIFIC_ATTRS[tagName]?.has(rawAttrName)
                    ) {
                        allowed = true;
                    }
                }

                // Drop always-unsafe attributes for standard HTML elements
                if (isAlwaysUnsafeAttribute(attrName, attr.value)) {
                    allowed = false;
                }
            }

            // Validate URL protocols
            if (
                allowed &&
                (URL_ATTRIBUTES.has(attrName) ||
                    URL_ATTRIBUTES.has(rawAttrName))
            ) {
                // Exception: Allow data:image/png URIs for SVG <image> elements (used in icon sprite)
                const isSvgImageDataUri =
                    tagName === 'image' &&
                    (attrName === 'xlink:href' || attrName === 'href') &&
                    attr.value.startsWith('data:image/png');

                if (!isSvgImageDataUri) {
                    try {
                        const url = new URL(attr.value, baseUrl);
                        if (!ALLOWED_PROTOCOLS.has(url.protocol)) {
                            allowed = false;
                        }
                    } catch {
                        // Invalid URL, skip it
                        allowed = false;
                    }
                }
            }

            if (allowed) {
                sanitized.setAttribute(rawAttrName, attr.value);
            }
        }

        // Recursively sanitize child nodes
        for (const child of Array.from(element.childNodes)) {
            const sanitizedChild = sanitizeNode(child);
            if (sanitizedChild) {
                sanitized.appendChild(sanitizedChild);
            }
        }

        return sanitized;
    };

    // Sanitize all nodes in the body
    const sanitizedFragment = document.createDocumentFragment();
    for (const child of Array.from(doc.body.childNodes)) {
        const sanitizedChild = sanitizeNode(child);
        if (sanitizedChild) {
            sanitizedFragment.appendChild(sanitizedChild);
        }
    }

    // Convert back to HTML string
    const tempDiv = document.createElement('div');
    tempDiv.appendChild(sanitizedFragment);
    return tempDiv.innerHTML;
};

/**
 * Sets pre-sanitized HTML content on an element. Behind the hood uses the sanitizeHTML function, uses the same API.
 * @memberof Sanitizer
 * @function
 * @param {Element} element - The element to set HTML on
 * @param {string} html - The HTML content to set
 * @param {Set<string> | false} [allowedTags] - A configurable set of allowed tags. Set to false to skip tag validation (except for a short list of dangerous tags that are always stripped)
 * @param {Set<string> | false} [allowedAttrs] - A set of allowed attributes (applies to all tags). Set to false to allow all attributes (except always-unsafe ones)
 * @returns {Element} The element with HTML set
 */
export const setHTMLSafe = (
    element: Element,
    html: string,
    allowedTags: Set<string> | false = SANITIZER_DEFAULT_ALLOWED_TAGS,
    allowedAttrs: Set<string> | false = SANITIZER_DEFAULT_ALLOWED_ATTRS
): Element => {
    element.innerHTML = sanitizeHTML(html, allowedTags, allowedAttrs);

    return element;
};

/**
 * @memberof SharedComponents
 * @class Sanitizer
 * @alias sanitizer
 */
